import { createContext, type ReactNode, useState } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useDialog } from '@ics-portal/guardian';
import { type Phase, type TraBase } from '@ics-portal/tra';
import { PhaseName } from '@ics-portal/tra/src/v1/phases';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/GlobalUiContext';
import useTRA from '../hooks/use-tra';
import {
    type PhaseActivationState,
    TraPhaseHelper,
} from '../helpers/tra-phase-helper';
import useTRALock from '../hooks/use-tra-lock';
import { usePhaseComplete } from '../hooks/use-phase-complete';

export interface TRAPhaseContextValues {
    setCurrentPhase: (phase: Phase) => void;
    currentPhase?: Phase;
    currentRootPhase?: Phase;
    rootPhases: Phase[];
    currentChildPhases: Phase[];
    gotoNextPhase: () => void;
}

const TRAsPhaseContext = createContext<TRAPhaseContextValues>({
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- default value
    setCurrentPhase: () => {},
    rootPhases: [],
    currentChildPhases: [],
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- default value
    gotoNextPhase: () => {},
});

export function TRAPhaseProvider<T extends TraBase>({
    children,
}: {
    children: ReactNode;
}): ReactNode {
    const { data, asyncUpdateTra } = useTRA<T>();
    const { traLocked } = useTRALock();
    const { pushErrorToast } = useToast();
    const { isComplete } = usePhaseComplete();
    const { open, confirm, handleSubmit, close } = useDialog();
    const { search, pathname } = useLocation();
    const navigate = useNavigate();

    const [currentPhase, setCurrentPhase] = useState<Phase | undefined>(
        undefined,
    );

    const phaseHelper = useMemo(() => {
        if (!data) {
            return null;
        }
        return new TraPhaseHelper<T>(data);
    }, [data]);

    const currentRootPhase = useMemo(() => {
        if (!currentPhase?.rootPhaseId) {
            return currentPhase;
        }
        return data?.phases.getById(currentPhase.rootPhaseId);
    }, [currentPhase, data?.phases]);

    const rootPhases = useMemo(() => {
        return data?.phases.getRootPhases() ?? [];
    }, [data]);

    const currentChildPhases = useMemo(() => {
        if (!currentRootPhase) {
            return [];
        }
        return data?.phases.getChildPhases(currentRootPhase) ?? [];
    }, [currentRootPhase, data?.phases]);

    /**
     * Lock the phases that are required to be locked
     */
    const lockPhases = useCallback(
        (phaseToLock: Phase): void => {
            const lockRule = data?.phaseLockRules[phaseToLock.name];
            if (!lockRule) {
                return;
            }
            lockRule.locking.forEach((phaseName) => {
                const phase = data.phases
                    .getAll()
                    .find((p) => p.name === phaseName);
                if (phase) {
                    phase.lock();
                    data.phases.update(phase);
                }
            });
        },
        [data],
    );

    const finalizePhaseChange = useCallback(
        async (phase: Phase, update?: boolean): Promise<void> => {
            if (!currentPhase || !data) {
                return;
            }
            data.phases.setCurrentPhase(phase.id);
            setCurrentPhase(phase);
            if (
                (parseInt(currentPhase.id) < parseInt(phase.id) &&
                    !isComplete(currentPhase)) ||
                update
            ) {
                await asyncUpdateTra();
            }
        },
        [asyncUpdateTra, currentPhase, data, isComplete],
    );

    const handlePhaseCompleteNotification = useCallback(
        (activationState: PhaseActivationState) => {
            const content = (
                <ul>
                    {activationState.error?.map((e) => <li key={e}>{e}</li>)}
                </ul>
            );

            if (currentPhase?.id !== activationState.phase.id) {
                confirm({
                    title: `${activationState.phase.name} not complete`,
                    content,
                    buttons: {
                        submitLabel: `Go to ${activationState.phase.name}`,
                    },
                });
                handleSubmit(async () => {
                    await finalizePhaseChange(activationState.phase);
                    close();
                });
                return;
            }
            open({
                title: 'Phase incomplete',
                content,
            });
        },
        [
            finalizePhaseChange,
            close,
            confirm,
            currentPhase?.id,
            handleSubmit,
            open,
        ],
    );

    const handlePhaseToLockNotification = useCallback(
        (phaseToLock: PhaseActivationState) => {
            confirm({
                title: `To the next step`,
                content: (
                    <>
                        <p>
                            Once you proceed, all previous steps will be locked
                            and available for viewing only. They cannot be
                            edited.
                        </p>
                        <div>Are you sure you want to proceed?</div>
                    </>
                ),
                buttons: {
                    submitLabel: `Proceed`,
                },
            });
            handleSubmit(async () => {
                lockPhases(phaseToLock.phase);
                await finalizePhaseChange(phaseToLock.nextPhase, true);
                close();
            });
        },
        [
            finalizePhaseChange,
            close,
            confirm,
            data?.phases,
            handleSubmit,
            lockPhases,
        ],
    );

    /**
     * Set the current phase
     * @param phase - phase to set as current phase
     */
    const setCurrentPhaseHandler = useCallback(
        (activatedPhase: Phase): void => {
            if (!data || !currentPhase || !phaseHelper) {
                return;
            }
            const selectedPhase = phaseHelper.childPhase(activatedPhase);

            const nextPhase = phaseHelper.getNextPossiblePhase(selectedPhase);

            if (traLocked?.locked) {
                data.phases.setCurrentPhase(nextPhase.id);
                setCurrentPhase(nextPhase);
                return;
            }

            const activationState = phaseHelper.hasPhaseToComplete({
                nextPhase,
                selectedPhase,
            });

            const phaseToLock = phaseHelper.hasPhaseToLock(
                nextPhase,
                currentPhase,
            );

            if (phaseToLock) {
                handlePhaseToLockNotification(phaseToLock);
                return;
            }

            if (activationState.error) {
                handlePhaseCompleteNotification(activationState);
                return;
            }

            finalizePhaseChange(nextPhase).catch(() => {
                pushErrorToast({
                    detail: 'Failed to save settings',
                });
            });
        },
        [
            data,
            currentPhase,
            phaseHelper,
            traLocked,
            finalizePhaseChange,
            handlePhaseToLockNotification,
            handlePhaseCompleteNotification,
            pushErrorToast,
        ],
    );

    /**
     * Go to the next phase
     */
    const gotoNextPhase = useCallback((): void => {
        const nextPhase = data?.phases.getById(currentPhase?.nextPhaseId);
        if (!nextPhase) {
            return;
        }
        setCurrentPhaseHandler(nextPhase);
    }, [currentPhase?.nextPhaseId, data?.phases, setCurrentPhaseHandler]);

    function getValidPhaseQuery(): PhaseName | undefined {
        const params = new URLSearchParams(search);
        const phaseQuery = params.get('phase');
        const isValidPhaseQuery = (name: string | null): name is PhaseName =>
            Object.values(PhaseName).includes(phaseQuery as PhaseName);

        return isValidPhaseQuery(phaseQuery) ? phaseQuery : undefined;
    }

    function handlePhaseQuery(phaseQuery: PhaseName): void {
        const foundRelationPhase = data?.phases.getByName(phaseQuery);
        if (!foundRelationPhase) {
            return;
        }

        setCurrentPhase(foundRelationPhase);

        const params = new URLSearchParams(search);
        params.delete('phase');
        navigate(
            {
                pathname: location.pathname,
                search: params.toString(),
            },
            { replace: true },
        );
    }

    useEffect(() => {
        const dataCurrentPhase = data?.phases.getCurrentPhase();
        if (!dataCurrentPhase || !phaseHelper) {
            return;
        }
        const dataActivationState = phaseHelper.hasPhaseToComplete({
            nextPhase: dataCurrentPhase,
            selectedPhase: dataCurrentPhase,
        });

        if (!dataActivationState.error) {
            const validPhaseQuery = getValidPhaseQuery();
            if (validPhaseQuery) {
                handlePhaseQuery(validPhaseQuery);
                return;
            }

            setCurrentPhase(dataActivationState.phase);
            return;
        }
        setCurrentPhase(data?.phases.getCurrentPhase());
    }, [data, phaseHelper]);

    const values = useMemo(
        () => ({
            setCurrentPhase: setCurrentPhaseHandler,
            currentRootPhase,
            currentPhase,
            rootPhases,
            currentChildPhases,
            gotoNextPhase,
        }),
        [
            currentChildPhases,
            currentPhase,
            currentRootPhase,
            gotoNextPhase,
            rootPhases,
            setCurrentPhaseHandler,
        ],
    );

    return (
        <TRAsPhaseContext.Provider value={values}>
            {children}
        </TRAsPhaseContext.Provider>
    );
}

export default TRAsPhaseContext;
